/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-underscore-dangle */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  DepartmentHeadings,
  DepartmentState as DeptStateEnum,
  ModeEnum,
  type Department,
  type ErrorData,
  type ErrorPayload,
} from '@types';
import { createDepartmentSuccess } from './chatAutoAssignment';

type RequestStatus =
  | 'idle'
  | 'pending'
  | 'success'
  | 'failure'
  | 'item-saved'
  | 'item-deleted';

export type DepartmentState = {
  loading: 'idle' | 'pending';
  isDepartmentModuleActive: boolean;
  items: Department[];
  error?: ErrorData | ErrorPayload;
  /** Departments tree (getDepartmentsTree) – separate from merchant departments */
  treeLoading: 'idle' | 'pending';
  treeError?: ErrorData | ErrorPayload;
  treeItems: Department[];
  /** The state field from the getDepartmentsTree response (ACTIVE / INACTIVE). */
  treeState?: DeptStateEnum;
  /** The id field from the getDepartmentsTree response (department tree ID). */
  treeId?: number;
  /** Overrides for responseMessage set after editing a department type.
   *  Applied after every tree re-fetch so stale API data doesn't revert the type. */
  responseMessageOverrides: Record<number, { type?: string }>;
  /** Parent IDs whose children were detached. Applied after tree re-fetch
   *  so stale API data doesn't re-attach them. */
  detachedParentIds: number[];
  requestVisibility: 'idle' | 'pending' | 'success' | 'failure';
  headings: {
    items: DepartmentHeadings[];
    request: RequestStatus;
  };
};

export type DepartmentsHeadingListItem = {
  key: string;
  name: string;
  provider: string;
  headerText: string;
};

export type DepartmentHeadingsType = {
  mode: 'create' | 'delete' | 'list';
  bizProcessTreeId: number;
  botAccountId: number;
  data?: DepartmentHeadings;
};

const initialState: DepartmentState = {
  loading: 'idle',
  isDepartmentModuleActive: false,
  items: [],
  error: undefined,
  treeLoading: 'idle',
  treeError: undefined,
  treeItems: [],
  treeState: undefined,
  treeId: undefined,
  responseMessageOverrides: {},
  detachedParentIds: [],
  requestVisibility: 'idle',
  headings: {
    items: [],
    request: 'idle',
  },
};

export const departmentsSlice = createSlice({
  name: 'departments',
  initialState,
  reducers: {
    setDepartmentModuleActive(state, action: PayloadAction<boolean>) {
      state.isDepartmentModuleActive = action.payload;
    },
    fetchDepartments(state) {
      state.loading = 'pending';
      state.error = undefined;
      state.items = [];
    },
    fetchDepartmentsTree(state) {
      state.treeLoading = 'pending';
      state.treeError = undefined;
      // Keep previous treeItems so the canvas stays mounted during refetch (e.g. after reorder)
    },
    fetchDepartmentsTreeSuccess(
      state,
      action: PayloadAction<{
        items: Department[];
        treeState?: DeptStateEnum;
        treeId?: number;
      }>
    ) {
      state.treeState = action.payload.treeState;
      if (action.payload.treeId != null) {
        state.treeId = action.payload.treeId;
      }
      const itemsDep = action.payload.items.reduce(
        (items, department) => {
          items[department.state].push(department);
          return items;
        },
        { ACTIVE: [] as Department[], INACTIVE: [] as Department[] }
      );

      itemsDep.ACTIVE.sort((l, r) => l.name.localeCompare(r.name));
      itemsDep.INACTIVE.sort((l, r) => l.name.localeCompare(r.name));

      const items = [...itemsDep.ACTIVE, ...itemsDep.INACTIVE];

      {
        const standardDepartmentIndex = items.findIndex(d =>
          d.name.startsWith('BotStandardServices')
        );
        if (standardDepartmentIndex !== -1) {
          const stantardDepartment = items.splice(
            standardDepartmentIndex,
            1
          )[0];

          stantardDepartment.name = 'BotStandardServices';
          items.unshift(stantardDepartment);
        }

        const itemsRecord = items.reduce<Record<number, Department>>(
          (record, department) => {
            if (!department.departmentId) return record;

            return { ...record, [department.departmentId]: department };
          },
          {}
        );

        Object.values(itemsRecord).forEach(department => {
          const { parentNodeId } = department;
          const parent =
            parentNodeId !== null ? itemsRecord[parentNodeId] : undefined;

          if (parent) {
            parent.childrens = [...(parent.childrens || []), department];
          }
        });

        items.forEach(department => {
          if (department.parentNodeId)
            department.parentName = itemsRecord[department.parentNodeId].name;
        });

        items.sort((l, r) =>
          (l.parentName || '').localeCompare(r.parentName || '')
        );
      }
      // Always apply responseMessage overrides from recent edits so stale
      // API data doesn't revert type changes or lose flow form data.
      // Overrides are cleared when a new save produces a fresh override
      // for the same department, or on full page reload.
      items.forEach(item => {
        const id = item.departmentId ?? item.id;
        if (id != null && state.responseMessageOverrides[id]) {
          item.responseMessage = state.responseMessageOverrides[id];
        }
      });
      // Apply detached-parent overrides so stale API data doesn't
      // re-attach children that were moved to root level.
      if (state.detachedParentIds.length > 0) {
        const detached = new Set(state.detachedParentIds);
        items.forEach(item => {
          if (item.parentNodeId != null && detached.has(item.parentNodeId)) {
            item.parentNodeId = null;
          }
        });
        items.forEach(item => {
          const id = item.departmentId ?? item.id;
          if (id != null && detached.has(id)) {
            item.childrens = [];
          }
        });
        state.detachedParentIds = [];
      }
      state.treeItems = items;
    },
    fetchDepartmentsTreeFailure(state, action: PayloadAction<ErrorData>) {
      state.treeError = action.payload;
    },
    fetchDepartmentsTreeFulfill(state) {
      state.treeLoading = 'idle';
    },
    fetchDepartmentsSuccess(state, action: PayloadAction<Department[]>) {
      const itemsDep = action.payload.reduce(
        (items, department) => {
          items[department.state].push(department);
          return items;
        },
        { ACTIVE: [] as Department[], INACTIVE: [] as Department[] }
      );

      itemsDep.ACTIVE.sort((l, r) => l.name.localeCompare(r.name));
      itemsDep.INACTIVE.sort((l, r) => l.name.localeCompare(r.name));

      const items = [...itemsDep.ACTIVE, ...itemsDep.INACTIVE];

      {
        const standardDepartmentIndex = items.findIndex(d =>
          d.name.startsWith('BotStandardServices')
        );
        if (standardDepartmentIndex !== -1) {
          const stantardDepartment = items.splice(
            standardDepartmentIndex,
            1
          )[0];

          stantardDepartment.name = 'BotStandardServices';
          items.unshift(stantardDepartment);
        }

        const itemsRecord = items.reduce<Record<number, Department>>(
          (record, department) => {
            if (!department.departmentId) return record;

            return { ...record, [department.departmentId]: department };
          },
          {}
        );

        Object.values(itemsRecord).forEach(department => {
          const { parentNodeId } = department;
          const parent =
            parentNodeId !== null ? itemsRecord[parentNodeId] : undefined;

          if (parent) {
            parent.childrens = [...(parent.childrens || []), department];
          }
        });

        items.forEach(department => {
          if (department.parentNodeId)
            department.parentName = itemsRecord[department.parentNodeId].name;
        });

        items.sort((l, r) =>
          (l.parentName || '').localeCompare(r.parentName || '')
        );
      }
      state.items = items;
    },
    fetchDepartmentsFailure(state, action: PayloadAction<ErrorData>) {
      state.error = action.payload;
    },
    fetchDepartmentsFulfill(state) {
      state.loading = 'idle';
    },
    fetchDepartmentVisibility(
      state,
      _action: PayloadAction<{ departmentId: number; visible: boolean }>
    ) {
      state.requestVisibility = 'pending';
    },
    fetchDepartmentVisibilitySuccess(
      state,
      action: PayloadAction<{ departmentId: number }>
    ) {
      const { items, treeItems } = state;
      state.items = items.map((department: Department) => {
        const { departmentId, hiddenToContacts } = department;
        if (departmentId === action.payload.departmentId)
          department.hiddenToContacts = !hiddenToContacts;
        return department;
      });
      state.treeItems = treeItems.map((department: Department) => {
        const { departmentId, hiddenToContacts } = department;
        if (departmentId === action.payload.departmentId)
          department.hiddenToContacts = !hiddenToContacts;
        return department;
      });
      state.requestVisibility = 'success';
    },
    fetchDepartmentVisibilityFailure(
      state,
      action: PayloadAction<ErrorPayload>
    ) {
      state.error = action.payload as ErrorPayload<'OPERATION_FAILED'>;
      state.requestVisibility = 'failure';
    },
    fetchDepartmentVisibilityFulfill(state) {
      state.requestVisibility = 'idle';
    },
    fetchDepartmentHeadings(
      state,
      _action: PayloadAction<DepartmentHeadingsType>
    ) {
      state.headings.request = 'pending';
    },
    fetchDepartmentHeadingsSuccess(
      state,
      action: PayloadAction<{
        items: DepartmentHeadings[];
        mode: string;
        botAccountId?: number;
      }>
    ) {
      const { items, mode, botAccountId } = action.payload;
      const { headings } = state;
      let request = mode === ModeEnum.CREATE ? 'item-saved' : 'success';

      if (ModeEnum.LIST === mode) state.headings.items = [...items];

      if (ModeEnum.CREATE === mode)
        state.headings.items = [...headings.items, ...items];

      if (mode === ModeEnum.DELETE) {
        state.headings.items = headings.items.filter(
          (item: DepartmentHeadings) => item.botAccountId !== botAccountId
        );
        request = 'item-deleted';
      }
      state.headings.request = request as RequestStatus;
    },
    fetchDepartmentHeadingsFailure(state, action: PayloadAction<ErrorPayload>) {
      state.error = action.payload as ErrorPayload<'OPERATION_FAILED'>;
      state.headings.request = 'failure';
    },
    changeRequestStatus(state, action: PayloadAction<string>) {
      state.headings.request = action.payload as RequestStatus;
    },
    /** Optimistically detach all children of a parent node in treeItems
     *  (set their parentNodeId to null so they become root-level). */
    detachChildrenFromParent(state, action: PayloadAction<number>) {
      const parentId = action.payload;
      // Find the highest `number` among current root-level nodes so detached
      // children are placed after them (rightmost) instead of at position 0.
      let maxRootNumber = 0;
      state.treeItems.forEach(item => {
        if (item.parentNodeId == null && item.number > maxRootNumber) {
          maxRootNumber = item.number;
        }
      });
      let nextNumber = maxRootNumber + 1;
      state.treeItems.forEach(item => {
        if (item.parentNodeId === parentId) {
          item.parentNodeId = null;
          item.number = nextNumber;
          nextNumber += 1;
        }
      });
      const parent = state.treeItems.find(
        d => (d.departmentId ?? d.id) === parentId
      );
      if (parent) {
        parent.childrens = [];
      }
      // Record so the detachment survives tree re-fetches.
      if (!state.detachedParentIds.includes(parentId)) {
        state.detachedParentIds.push(parentId);
      }
    },
  },
  extraReducers: builder => {
    builder.addCase(createDepartmentSuccess, (state, action) => {
      const saved = action.payload;
      const id = saved.departmentId ?? saved.id;
      if (id == null || !saved.responseMessage) return;
      // Store override so subsequent tree re-fetches also get patched.
      state.responseMessageOverrides[id] = saved.responseMessage;
      // Patch current treeItems immediately so the graph card reflects the
      // saved state before the full tree re-fetch completes.
      const item = state.treeItems.find(d => (d.departmentId ?? d.id) === id);
      if (item) {
        item.responseMessage = saved.responseMessage;
        if (saved.agentIds != null) {
          item.agentIds = saved.agentIds;
        }
      }
    });
  },
});

export default departmentsSlice.reducer;

export const {
  fetchDepartments,
  fetchDepartmentsTree,
  fetchDepartmentsTreeSuccess,
  fetchDepartmentsTreeFailure,
  fetchDepartmentsTreeFulfill,
  fetchDepartmentsFailure,
  fetchDepartmentsSuccess,
  fetchDepartmentsFulfill,
  setDepartmentModuleActive,
  fetchDepartmentVisibility,
  fetchDepartmentVisibilitySuccess,
  fetchDepartmentVisibilityFailure,
  fetchDepartmentVisibilityFulfill,
  fetchDepartmentHeadings,
  fetchDepartmentHeadingsSuccess,
  fetchDepartmentHeadingsFailure,
  changeRequestStatus,
  detachChildrenFromParent,
} = departmentsSlice.actions;
